/**
 * Chat machine. Pure reducer over a discriminated union state.
 *
 * Per docs/frontend-react-guidelines.md §5.3 and §7:
 *  - This reducer is THE single source of truth for the chat state.
 *  - Animation phases ARE reducer states (drawing, landing). They are advanced
 *    by exactly three things and NEVER by an Effect watching state:
 *      1. the composer submit handler            (idle -> drawing)
 *      2. the /answer response promise resolving  (drawing -> landing)
 *      3. the morph animation-end callback        (landing -> chat-active)
 *  - Display flags (isLoading, hasStarted, etc.) are computed during render
 *    from `state.kind` — they are NOT stored in state.
 *
 * Task 074 (landing-shot loading sequence): on the FIRST query the idle scene
 * stays mounted through `drawing` and `landing` (hasStarted is false for both),
 * so the multi-turn chat layout does not appear until the arrow lands. The
 * `drawing` phase is the loading state for /answer latency — it has NO timer;
 * it holds until `answer/received` arrives. Only `landing` is time-boxed (the
 * arrow's flight), advanced by the morph's animation-end callback.
 *
 * Transitions diagram:
 *
 *   idle
 *     -> drawing            (action: composer/submit)
 *
 *   drawing                 (idle scene held; arrow draws + charges; request in flight)
 *     -> landing            (action: answer/received when reducedMotion=false)
 *     -> chat-active        (action: answer/received when reducedMotion=true —
 *                            the reducer skips the landing animation entirely)
 *     -> errored            (action: answer/error)
 *
 *   landing                 (arrow releases/flies/lands; idle fading out)
 *     -> chat-active        (action: motion/landing-complete)
 *
 *   chat-active
 *     -> chat-active        (action: chat/send-next pending,
 *                            then answer/received append, or answer/error)
 *
 *   errored
 *     -> idle | chat-active (action: error/dismiss — returns to the previous
 *                            shell so the user can retry)
 *
 *   (any)
 *     -> idle               (action: chat/reset — "Nueva conversación"; the
 *                            caller aborts any in-flight request first. Task 086)
 */
import type { AnswerError, AnswerResponse } from "../api/answer-client";

export interface UserMessage {
  id: string;
  role: "user";
  text: string;
}

/**
 * Assistant turn. Streaming-aware (Task 137):
 *  - status "streaming": `text` grows with each answer.delta; `response` is null
 *    (sources/model are not known until the final event) — render text + a live
 *    cursor, no sources.
 *  - status "complete": settled by answer.final (or the non-streaming
 *    answer/received fallback); `response` carries sources/model/route/cache and
 *    `text` === response.answer.
 *  - status "error": the stream failed AFTER some text arrived; keep the partial
 *    `text` (visually marked incomplete) and stash `error`.
 *  - status "stopped": the USER halted generation via the dock stop control;
 *    keep the partial `text`, `response` stays null, NOT an error. Settled and
 *    no longer loading.
 */
export type AssistantStatus = "streaming" | "complete" | "error" | "stopped";

export interface AssistantMessage {
  id: string;
  role: "assistant";
  text: string;
  status: AssistantStatus;
  response: AnswerResponse | null;
  error: AnswerError | null;
  /** Epoch ms when the answer settled — set only on the completed turn (the
   *  single build choke point); undefined while streaming or on error. */
  createdAt?: number;
  /** The server is re-checking a refused first answer; the loader says so. */
  rechecking?: boolean;
}

export type ChatMessage = UserMessage | AssistantMessage;

export interface PendingExchange {
  pendingId: string;
  query: string;
}

/**
 * Accumulator carried by the `landing` state while the arrow flies (Task 137).
 * Deltas arriving during the (short) flight land here and are handed to the
 * thread on motion/landing-complete. The non-streaming path seeds `final`
 * immediately so the same handoff builds a complete message.
 */
export interface StreamBuffer {
  text: string;
  final: AnswerResponse | null;
  error: AnswerError | null;
  rechecking?: boolean;
}

export type ChatState =
  | { kind: "idle" }
  | { kind: "drawing"; query: string; pendingId: string }
  | {
      kind: "landing";
      query: string;
      pendingId: string;
      stream: StreamBuffer;
    }
  | {
      kind: "chat-active";
      thread: ChatMessage[];
      pending: PendingExchange | null;
    }
  | {
      kind: "errored";
      error: AnswerError;
      thread: ChatMessage[];
      lastQuery: string;
    };

export type ChatAction =
  | { type: "composer/submit"; query: string; pendingId: string }
  | {
      type: "answer/received";
      pendingId: string;
      response: AnswerResponse;
      reducedMotion: boolean;
    }
  // Streaming (Task 137): start opens the assistant turn (drawing -> landing, or
  // -> chat-active under reduced motion / for follow-ups); delta appends text;
  // final settles sources/model; error settles a partial turn as incomplete.
  | { type: "answer/stream-start"; pendingId: string; reducedMotion: boolean }
  | { type: "answer/delta"; pendingId: string; text: string }
  // The server started a careful re-check of a refused first answer.
  | { type: "answer/rechecking"; pendingId: string }
  | { type: "answer/final"; pendingId: string; response: AnswerResponse }
  | { type: "answer/error"; pendingId: string; error: AnswerError }
  // User halted generation via the dock stop control. Settles the in-flight turn
  // keeping partial text; NOT an error. Carries no pendingId — the app guarantees
  // a single in-flight request at a time (activeRequestRef), so "the streaming /
  // pending turn" is unambiguous.
  | { type: "answer/stop" }
  | { type: "motion/landing-complete"; pendingId: string }
  | { type: "chat/send-next"; query: string; pendingId: string }
  | { type: "error/dismiss" }
  | { type: "chat/reset" };

export const initialChatState: ChatState = { kind: "idle" };

function buildUserMessage(query: string, pendingId: string): UserMessage {
  return { id: `u:${pendingId}`, role: "user", text: query };
}

function buildAssistantMessage(
  pendingId: string,
  response: AnswerResponse,
): AssistantMessage {
  return {
    id: `a:${pendingId}`,
    role: "assistant",
    text: response.answer,
    status: "complete",
    response,
    error: null,
    createdAt: Date.now(),
  };
}

function buildStreamingAssistant(pendingId: string): AssistantMessage {
  return { id: `a:${pendingId}`, role: "assistant", text: "", status: "streaming", response: null, error: null };
}

/** Build the settled assistant turn from a landing accumulator (Task 137). */
function assistantFromBuffer(pendingId: string, stream: StreamBuffer): AssistantMessage {
  if (stream.error !== null) {
    return { id: `a:${pendingId}`, role: "assistant", text: stream.text, status: "error", response: null, error: stream.error };
  }
  if (stream.final !== null) {
    return buildAssistantMessage(pendingId, stream.final);
  }
  return { id: `a:${pendingId}`, role: "assistant", text: stream.text, status: "streaming", response: null, error: null,
    ...(stream.rechecking ? { rechecking: true } : {}) };
}

/** Map the assistant turn for `pendingId` through `fn`; other messages untouched. */
function mapAssistant(
  thread: ChatMessage[],
  pendingId: string,
  fn: (message: AssistantMessage) => AssistantMessage,
): ChatMessage[] {
  const id = `a:${pendingId}`;
  return thread.map((message) =>
    message.role === "assistant" && message.id === id ? fn(message) : message,
  );
}

function hasStreamingAssistant(thread: ChatMessage[], pendingId: string): boolean {
  const id = `a:${pendingId}`;
  return thread.some(
    (message) => message.role === "assistant" && message.id === id && message.status === "streaming",
  );
}

export function chatReducer(state: ChatState, action: ChatAction): ChatState {
  switch (action.type) {
    case "composer/submit": {
      // First query only. Legal from idle, or from an errored first query
      // (empty thread) so the user can re-ask after a failure. Subsequent
      // messages use chat/send-next.
      if (state.kind === "idle") {
        return { kind: "drawing", query: action.query, pendingId: action.pendingId };
      }
      if (state.kind === "errored" && state.thread.length === 0) {
        return { kind: "drawing", query: action.query, pendingId: action.pendingId };
      }
      return state;
    }

    case "answer/received": {
      if (state.kind === "drawing" && state.pendingId === action.pendingId) {
        // Under reduced motion the reducer skips the landing animation —
        // drawing -> chat-active directly. The accessibility contract is
        // enforced HERE, not in any Effect. See guidelines §7.
        if (action.reducedMotion) {
          const userMsg = buildUserMessage(state.query, state.pendingId);
          const aiMsg = buildAssistantMessage(state.pendingId, action.response);
          return {
            kind: "chat-active",
            thread: [userMsg, aiMsg],
            pending: null,
          };
        }
        return {
          kind: "landing",
          query: state.query,
          pendingId: state.pendingId,
          stream: { text: action.response.answer, final: action.response, error: null },
        };
      }
      if (
        state.kind === "chat-active" &&
        state.pending !== null &&
        state.pending.pendingId === action.pendingId
      ) {
        const userMsg = buildUserMessage(state.pending.query, state.pending.pendingId);
        const aiMsg = buildAssistantMessage(state.pending.pendingId, action.response);
        return {
          kind: "chat-active",
          thread: [...state.thread, userMsg, aiMsg],
          pending: null,
        };
      }
      return state;
    }

    case "answer/stream-start": {
      // First query: open the assistant turn. Under reduced motion the landing
      // animation is skipped (drawing -> chat-active directly); otherwise the
      // accumulator rides the `landing` flight and is handed to the thread on
      // motion/landing-complete.
      if (state.kind === "drawing" && state.pendingId === action.pendingId) {
        if (action.reducedMotion) {
          return {
            kind: "chat-active",
            thread: [
              buildUserMessage(state.query, state.pendingId),
              buildStreamingAssistant(state.pendingId),
            ],
            pending: null,
          };
        }
        return {
          kind: "landing",
          query: state.query,
          pendingId: state.pendingId,
          stream: { text: "", final: null, error: null },
        };
      }
      // Follow-up: materialize the user turn + a streaming assistant turn, clear
      // pending. No morph/landing for follow-ups (the thread loader covered the
      // gap until the first delta).
      if (
        state.kind === "chat-active" &&
        state.pending !== null &&
        state.pending.pendingId === action.pendingId
      ) {
        return {
          kind: "chat-active",
          thread: [
            ...state.thread,
            buildUserMessage(state.pending.query, state.pending.pendingId),
            buildStreamingAssistant(state.pending.pendingId),
          ],
          pending: null,
        };
      }
      return state;
    }

    case "answer/delta": {
      if (state.kind === "landing" && state.pendingId === action.pendingId) {
        return { ...state, stream: { ...state.stream, text: state.stream.text + action.text } };
      }
      if (state.kind === "chat-active") {
        return {
          kind: "chat-active",
          thread: mapAssistant(state.thread, action.pendingId, (m) =>
            m.status === "streaming" ? { ...m, text: m.text + action.text } : m,
          ),
          pending: state.pending,
        };
      }
      return state;
    }

    case "answer/rechecking": {
      if (state.kind === "landing" && state.pendingId === action.pendingId) {
        return { ...state, stream: { ...state.stream, rechecking: true } };
      }
      if (state.kind === "chat-active") {
        return {
          kind: "chat-active",
          thread: mapAssistant(state.thread, action.pendingId, (m) =>
            m.status === "streaming" ? { ...m, rechecking: true } : m,
          ),
          pending: state.pending,
        };
      }
      return state;
    }

    case "answer/final": {
      if (state.kind === "landing" && state.pendingId === action.pendingId) {
        return {
          ...state,
          stream: { text: action.response.answer, final: action.response, error: null },
        };
      }
      if (state.kind === "chat-active") {
        return {
          kind: "chat-active",
          thread: mapAssistant(state.thread, action.pendingId, (m) => ({
            ...m,
            text: action.response.answer,
            status: "complete",
            response: action.response,
            error: null,
          })),
          pending: state.pending,
        };
      }
      return state;
    }

    case "answer/error": {
      // Pre-stream failure on the first query (no tokens shown yet): full-screen
      // error shell, same as the non-streaming path.
      if (state.kind === "drawing" && state.pendingId === action.pendingId) {
        return {
          kind: "errored",
          error: action.error,
          thread: [],
          lastQuery: state.query,
        };
      }
      // Error during the arrow flight: stash on the accumulator; landing-complete
      // surfaces it as an incomplete turn.
      if (state.kind === "landing" && state.pendingId === action.pendingId) {
        return { ...state, stream: { ...state.stream, text: action.error.discardPartial ? "" : state.stream.text, error: action.error } };
      }
      if (state.kind === "chat-active") {
        // A streaming turn already shown -> settle it as incomplete in place,
        // keeping the partial text (Task 133/137). Stay in chat-active.
        if (hasStreamingAssistant(state.thread, action.pendingId)) {
          return {
            kind: "chat-active",
            thread: mapAssistant(state.thread, action.pendingId, (m) => ({
              ...m,
              status: "error",
              text: action.error.discardPartial ? "" : m.text,
              error: action.error,
            })),
            pending: null,
          };
        }
        // Pre-stream failure on a follow-up (pending, no tokens yet): error shell.
        if (state.pending !== null && state.pending.pendingId === action.pendingId) {
          return {
            kind: "errored",
            error: action.error,
            thread: state.thread,
            lastQuery: state.pending.query,
          };
        }
      }
      return state;
    }

    case "answer/stop": {
      // The caller (handleStop) aborts the request FIRST, so the stream
      // callbacks early-return on the aborted signal and no further delta/final/
      // error dispatches land. Here we just settle the visible turn.
      if (state.kind !== "chat-active") return state;
      // A turn already streaming tokens -> settle it as stopped, keep the
      // partial text. (response stays null; sources were never delivered.)
      if (threadIsStreaming(state.thread)) {
        return {
          kind: "chat-active",
          thread: state.thread.map((message) =>
            message.role === "assistant" && message.status === "streaming"
              ? { ...message, status: "stopped" }
              : message,
          ),
          pending: null,
        };
      }
      // Stopped during the pre-token "thinking" phase (pending, no turn yet):
      // materialize the user turn + an empty stopped assistant turn so the
      // question is preserved in the thread and clearly marked halted.
      if (state.pending !== null) {
        return {
          kind: "chat-active",
          thread: [
            ...state.thread,
            buildUserMessage(state.pending.query, state.pending.pendingId),
            {
              id: `a:${state.pending.pendingId}`,
              role: "assistant",
              text: "",
              status: "stopped",
              response: null,
              error: null,
            },
          ],
          pending: null,
        };
      }
      return state;
    }

    case "motion/landing-complete": {
      if (state.kind === "landing" && state.pendingId === action.pendingId) {
        const userMsg = buildUserMessage(state.query, state.pendingId);
        const aiMsg = assistantFromBuffer(state.pendingId, state.stream);
        return {
          kind: "chat-active",
          thread: [userMsg, aiMsg],
          pending: null,
        };
      }
      return state;
    }

    case "chat/send-next": {
      if (state.kind !== "chat-active") return state;
      if (state.pending !== null) return state;
      return {
        kind: "chat-active",
        thread: state.thread,
        pending: { pendingId: action.pendingId, query: action.query },
      };
    }

    case "error/dismiss": {
      if (state.kind !== "errored") return state;
      if (state.thread.length === 0) {
        return { kind: "idle" };
      }
      return { kind: "chat-active", thread: state.thread, pending: null };
    }

    case "chat/reset": {
      // New conversation (Task 086): discard the thread and return to idle from
      // ANY state. The caller (handleNewChat) aborts any in-flight request
      // first; language + URL params live outside the reducer and are untouched.
      // Replaces the old window.location.reload() — no page reload.
      return { kind: "idle" };
    }

    default: {
      const exhaustive: never = action;
      void exhaustive;
      return state;
    }
  }
}

// Render-time helpers. Keep these as pure functions of state so callers
// don't reach for useState/useEffect to mirror them.
export function threadIsStreaming(thread: ChatMessage[]): boolean {
  return thread.some((m) => m.role === "assistant" && m.status === "streaming");
}

export function isLoading(state: ChatState): boolean {
  if (state.kind === "drawing") return true;
  if (state.kind === "landing") return true;
  // chat-active is "busy" while a follow-up is pending OR a turn is still
  // streaming tokens — the composer stays disabled until the answer settles.
  if (state.kind === "chat-active") return state.pending !== null || threadIsStreaming(state.thread);
  return false;
}

export function pendingQuery(state: ChatState): string | null {
  if (state.kind === "drawing") return state.query;
  if (state.kind === "landing") return state.query;
  if (state.kind === "chat-active" && state.pending !== null) {
    return state.pending.query;
  }
  return null;
}

/**
 * The question whose answer is currently being produced — pending OR still
 * streaming (Task 137). Used by the SourceRail to hold its "loading / sources
 * withheld until final" state through the whole stream. Distinct from
 * pendingQuery(), which is ONLY the not-yet-in-thread exchange the Thread
 * renders as a loader (returning the streaming query there would duplicate the
 * turn already in the thread).
 */
export function activeQuery(state: ChatState): string | null {
  if (state.kind === "drawing" || state.kind === "landing") return state.query;
  if (state.kind === "chat-active") {
    if (state.pending !== null) return state.pending.query;
    for (let i = state.thread.length - 1; i >= 0; i -= 1) {
      const m = state.thread[i];
      if (m !== undefined && m.role === "assistant" && m.status === "streaming") {
        const prev = state.thread[i - 1];
        return prev !== undefined && prev.role === "user" ? prev.text : null;
      }
    }
  }
  return null;
}

export function getThread(state: ChatState): ChatMessage[] {
  if (state.kind === "chat-active") return state.thread;
  if (state.kind === "errored") return state.thread;
  return [];
}

export function getError(state: ChatState): AnswerError | null {
  return state.kind === "errored" ? state.error : null;
}

// "Has the chat shell taken over?" — false through `drawing` and `landing`
// (Task 074: the idle scene stays mounted until the arrow lands), true once
// `chat-active` (or an errored thread that already had turns) is reached.
export function hasStarted(state: ChatState): boolean {
  if (state.kind === "chat-active") return true;
  if (state.kind === "errored" && state.thread.length > 0) return true;
  return false;
}
